import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "files",
    "uploadForm",
    "blobs",
    "documentsList",
    "documentsListItems",
    "processingStages",
    "claimsDropZone",
    "newClaimDropTarget"
  ];

  connect() {
    // Set up click-to-select files.
    this.uploadFormTarget.addEventListener("click", this.pickFiles.bind(this));

    // Set up drag-n-drop for file uploads.
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.uploadFormTarget.addEventListener(eventName, this.dragHandler.bind(this), false);
    });

    // Set up drag-n-drop for claims drop zone (creating new claims from documents).
    if (this.hasClaimsDropZoneTarget) {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        this.claimsDropZoneTarget.addEventListener(eventName, this.claimsDropZoneHandler.bind(this), false);
      });
    }

    // Watch for Turbo Stream additions to claims-list to auto-show it
    const claimsList = document.getElementById("claims-list");
    if (claimsList) {
      this.claimsListObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.addedNodes.length > 0) {
            // Check if any real claim nodes were added (not just text nodes)
            const hasClaimNode = Array.from(mutation.addedNodes).some(
              node => node.nodeType === Node.ELEMENT_NODE && node.id && node.id.startsWith("claim-")
            );
            if (hasClaimNode) {
              this.showClaimsList();
            }
            break;
          }
        }
      });
      this.claimsListObserver.observe(claimsList, { childList: true });
    }
  }

  disconnect() {
    if (this.claimsListObserver) {
      this.claimsListObserver.disconnect();
    }
  }

  showClaimsList() {
    const claimsList = document.getElementById("claims-list");
    if (claimsList) {
      claimsList.classList.remove("hidden");
      claimsList.classList.add("p-6", "pb-24", "flex", "flex-col", "gap-4");
    }
    const emptyState = document.getElementById("claims-empty-state");
    if (emptyState) {
      emptyState.classList.add("hidden");
    }
  }

  dragHandler(e) {
    e.preventDefault();
    e.stopPropagation();

    if (['dragenter', 'dragover'].includes(e.type)) {
      this.uploadFormTarget.classList.add("bg-light-gray", "border-black");
    } else {
      this.uploadFormTarget.classList.remove("bg-light-gray", "border-black");
    }

    if (e.type === 'drop') {
      this.uploadFiles(e.dataTransfer.files);
    }
  }

  pickFiles(e) {
    this.filesTarget.click();
  }

  filesPicked(e) {
    this.uploadFiles(e.target.files);
  }

  uploadFiles(files) {
    var numUploaders = 0;
    var numFinished = 0;

    // Clear out the blob-metadata for any files we uploaded before.
    this.blobsTarget.innerHTML = "";

    // Show the documents list if it was hidden (first upload)
    this.documentsListTarget.classList.remove("hidden");
    this.documentsListTarget.classList.add("flex", "flex-col", "gap-4");

    // Hide the processing stages placeholder
    if (this.hasProcessingStagesTarget) {
      this.processingStagesTarget.classList.add("hidden");
    }

    for (var i = 0; i < files.length; i++) {
      const file = files[i];

      // Create a temporary card to show upload progress
      const card = document.createElement('div');
      card.id = `uploading-${file.name}`;
      card.className = 'border rounded-lg p-4 bg-white';
      card.innerHTML = `
        <div class="flex items-center justify-between">
          <div class="font-medium">${file.name}</div>
          <div class="flex items-center gap-2">
            <div class="animate-spin h-4 w-4 border-2 border-medium-gray border-t-black rounded-full"></div>
            <span class="text-medium-gray" data-progress>Uploading...</span>
          </div>
        </div>
      `;
      this.documentsListItemsTarget.prepend(card);
      const progressNode = card.querySelector("[data-progress]");

      var uploader = new Uploader(file, progressNode, this.blobsTarget);

      numUploaders++;

      uploader.upload((success, error) => {
        numFinished++;

        // Remove the temporary upload card
        card.remove();

        // If all uploads are finished, submit the form.
        if (numFinished == numUploaders) {
          const form = this.uploadFormTarget.querySelector('form');
          const fetchParams = {
            method: form.method,
            body: new FormData(form),
            headers: {
              Accept: "text/vnd.turbo-stream.html"
            }
          };

          fetch(form.action, fetchParams)
            .then(response => response.text())
            .then((html) => Turbo.renderStreamMessage(html));
        }
      });
    }
  }

  // Document drag handle activation - only allow drag when initiated from the handle
  documentHandleMouseDown(e) {
    // Find the parent draggable row and mark it as handle-activated
    const row = e.target.closest('[draggable="true"]');
    if (row) row.dataset.handleActive = "true";
  }

  documentHandleMouseUp(e) {
    const row = e.target.closest('[draggable="true"]');
    if (row) delete row.dataset.handleActive;
  }

  // Document drag handlers
  documentDragStart(e) {
    // Only allow drag if initiated from the grab handle
    if (e.target.dataset.handleActive !== "true") {
      e.preventDefault();
      return;
    }
    delete e.target.dataset.handleActive;

    const documentId = e.target.dataset.documentId;
    e.dataTransfer.setData("application/x-document-id", documentId);
    e.dataTransfer.effectAllowed = "move";
    e.target.classList.add("opacity-50");

    // Show the new claim drop target
    if (this.hasNewClaimDropTargetTarget) {
      this.newClaimDropTargetTarget.classList.remove("hidden");
    }
  }

  documentDragEnd(e) {
    e.target.classList.remove("opacity-50");
    delete e.target.dataset.handleActive;

    // Hide the new claim drop target
    if (this.hasNewClaimDropTargetTarget) {
      this.newClaimDropTargetTarget.classList.add("hidden");
      this.newClaimDropTargetTarget.classList.remove("border-core-purple-500", "bg-core-purple-50");
      this.newClaimDropTargetTarget.classList.add("border-support-dark-300");
    }

    // Reset any claim card drag-over styles
    document.querySelectorAll('[data-claim-id]').forEach(el => {
      this.resetClaimCardStyle(el);
    });
  }

  // Claim card drag event handlers (for dropping documents onto existing claims)
  claimDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types.includes("application/x-document-id")) return;

    const card = e.currentTarget;
    card.classList.remove("border-support-light-300", "border");
    card.classList.add("border-2", "border-dashed", "border-alert-red-400", "bg-alert-red-200");
  }

  claimDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
  }

  claimDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    // Only reset if leaving the card entirely (not entering a child element)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      this.resetClaimCardStyle(e.currentTarget);
    }
  }

  claimDrop(e) {
    e.preventDefault();
    e.stopPropagation();

    this.resetClaimCardStyle(e.currentTarget);

    const documentId = e.dataTransfer.getData("application/x-document-id");
    const claimId = e.currentTarget.dataset.claimId;
    if (documentId && claimId) {
      this.moveDocumentToClaim(documentId, claimId);
    }
  }

  resetClaimCardStyle(card) {
    card.classList.remove("border-2", "border-dashed", "border-alert-red-400", "bg-alert-red-200");
    card.classList.add("border", "border-support-light-300");
  }

  // New claim drop target handlers
  newClaimDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types.includes("application/x-document-id")) return;
    e.currentTarget.classList.remove("border-support-dark-300");
    e.currentTarget.classList.add("border-core-purple-500", "bg-core-purple-50");
  }

  newClaimDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
  }

  newClaimDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      e.currentTarget.classList.add("border-support-dark-300");
      e.currentTarget.classList.remove("border-core-purple-500", "bg-core-purple-50");
    }
  }

  newClaimDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add("border-support-dark-300");
    e.currentTarget.classList.remove("border-core-purple-500", "bg-core-purple-50");

    const documentId = e.dataTransfer.getData("application/x-document-id");
    if (documentId) {
      this.createClaimFromDocument(documentId);
    }
  }

  // Claims drop zone handler for creating new claims (empty state)
  claimsDropZoneHandler(e) {
    e.preventDefault();
    e.stopPropagation();

    const dropZone = this.claimsDropZoneTarget;

    if (['dragenter', 'dragover'].includes(e.type)) {
      // Only show drop indication if dragging a document
      if (e.dataTransfer.types.includes("application/x-document-id")) {
        dropZone.classList.add("border-core-purple-500", "bg-core-purple-50");
        dropZone.classList.remove("border-transparent");
      }
    } else if (e.type === 'dragleave') {
      dropZone.classList.remove("border-core-purple-500", "bg-core-purple-50");
      dropZone.classList.add("border-transparent");
    } else if (e.type === 'drop') {
      dropZone.classList.remove("border-core-purple-500", "bg-core-purple-50");
      dropZone.classList.add("border-transparent");

      const documentId = e.dataTransfer.getData("application/x-document-id");
      if (documentId) {
        this.createClaimFromDocument(documentId);
      }
    }
  }

  getPartnerSlug() {
    if (this.hasNewClaimDropTargetTarget) {
      return this.newClaimDropTargetTarget.dataset.partnerSlug;
    }
    if (this.hasClaimsDropZoneTarget) {
      return this.claimsDropZoneTarget.dataset.partnerSlug;
    }
    return null;
  }

  moveDocumentToClaim(documentId, claimId) {
    const partnerSlug = this.getPartnerSlug();
    if (!partnerSlug) return;

    const url = `/partners/${partnerSlug}/add_claims/move_document`;

    const formData = new FormData();
    formData.append("document_id", documentId);
    formData.append("target_type", "claim");
    formData.append("target_claim_id", claimId);

    fetch(url, {
      method: "PATCH",
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(response => {
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        return response.text();
      })
      .then(html => Turbo.renderStreamMessage(html))
      .catch(error => {
        console.error("Failed to move document to claim:", error);
        alert("Failed to move document. Please try again.");
      });
  }

  createClaimFromDocument(documentId) {
    const partnerSlug = this.getPartnerSlug();
    if (!partnerSlug) return;

    const url = `/partners/${partnerSlug}/add_claims/move_document`;

    const formData = new FormData();
    formData.append("document_id", documentId);
    formData.append("target_type", "new_claim");

    fetch(url, {
      method: "PATCH",
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        Turbo.renderStreamMessage(html);
        this.showClaimsList();
      })
      .catch(error => {
        console.error("Failed to create claim from document:", error)
        alert("Failed to create claim. Please try again.")
      });
  }
}

class Uploader {
  constructor(file, progressNode, blobsNode) {
    this.file = file;
    this.progressNode = progressNode;
    this.blobsNode = blobsNode;

    this.directUpload = new DirectUpload(this.file, "/rails/active_storage/direct_uploads", this);

    this.finished = false;
    this.error = null;
    this.blob = null;
  }

  upload(callback) {
    this.directUpload.create((error, blob) => {
      this.finished = true;
      this.error = error;
      this.blob = blob;

      if (error) {
        callback(false, error);
      } else {
        // Add the blob info to the form
        const hiddenField = document.createElement('input')
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("value", blob.signed_id);
        hiddenField.name = "files[]"
        this.blobsNode.appendChild(hiddenField)

        callback(true);
      }
    })
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress",
      event => this.directUploadDidProgress(event))
  }

  directUploadDidProgress(event) {
    const fractionDone = event.loaded / event.total;
    this.progressNode.innerText = Math.round(fractionDone * 100) + "%";
  }
}
